import { Currency, OrderDetails, User } from "common/contracts"
import { EAwardFlowStatus, ELocalStorageKeys, EOrderStatus } from "constants/enums"
import { statusDictionary } from "constants/orderStatusMappings"
import Decimal from "decimal.js"
import { localStorageUtil } from "utils/localStorageUtil"
import { getSelectProjectOptions } from "utils/projectsUtil"

import { ordersUtils } from "../utils"

import { OrderFilters, StatusOption } from "./types"

export type OrderTableRow = {
    project: string
    name: string
    price: Decimal
    date: string
    orderId: string
    playerId: string
    subscriptionId: string | undefined
    publisherPurchaseId: string | undefined
    transactionId: string
    paymentFlow: EAwardFlowStatus
    status: string | undefined
    currencySymbol: string
    id: string
    publisherId: string
    type: string
}

export const createOrderTableRow = (order: OrderDetails, currencyCodeToCurrencyMap: Map<string, Currency>): OrderTableRow => {
    const projects = getProjectsFromStorage();
    const projectName = projects?.find(project => project.publisherId === order.publisherId)?.publisherName;    // Get the currency multiplier
    const currency = currencyCodeToCurrencyMap.get(order.currency);
    const multiplier = currency?.multiplier || 1;

    const displayPrice = order.amountTotal.div(multiplier);

    return {
        project: projectName ?? '',
        name: order.bundleName,
        price: displayPrice,
        date: order.createdAt,
        orderId: order.orderId,
        playerId: order.playerId,
        subscriptionId: order.subscriptionId,
        publisherPurchaseId: order.publisherPurchaseId,
        transactionId: order.publisherPurchaseId ?? '',
        paymentFlow: order.awardFlow,
        status: order.state,
        id: order._id,
        type: order.offerSnapshot?.type ?? '',
        currencySymbol: order.currencySymbol,
        publisherId: order.publisherId
    }
}

export const getProjectsFromStorage = () => {
    return localStorageUtil.getAny<User>(
        ELocalStorageKeys.USER_DETAILS
    )?.projects
}

export const getDefaultSelectedProjects = (allowedPublishers: string[]): string[] => {
    const projectsDetails = getProjectsFromStorage()

    if (!projectsDetails) return []

    return getSelectProjectOptions(projectsDetails, allowedPublishers).map(
        (project) => project.value
    )
}

export const cleanFilters = (filters: OrderFilters): OrderFilters => {
    const cleaned: any = {}

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
            cleaned[key] = value
        }
    })

    return cleaned
}

export const updateQueryParam = (
    paramName: string,
    paramValue: string,
    location: any,
    navigate: any
) => {
    const queryParams = new URLSearchParams(location.search)

    if (paramValue) {
        queryParams.set(paramName, paramValue)
    } else {
        queryParams.delete(paramName)
    }

    const navigationOpt: { replace: boolean; state?: Record<string, boolean> } = {
        replace: true
    }

    if (location?.state) {
        navigationOpt.state = location.state
    }

    navigate(`${location.pathname}?${queryParams.toString()}`, navigationOpt)
}

export const resetQueryParams = (
    paramsToReset: string[],
    location: any,
    navigate: any
) => {
    const newParams = new URLSearchParams(location.search)

    paramsToReset.forEach((param) => newParams.delete(param))

    const navigationOpt: { replace: boolean; state?: Record<string, boolean> } = {
        replace: true
    }

    if (location?.state) {
        navigationOpt.state = location.state
    }

    navigate(`${location.pathname}?${newParams.toString()}`, navigationOpt)
}

export const filterStatusesByOrderType = (statuses: EOrderStatus[], isFree?: boolean): EOrderStatus[] => {
    if (isFree) {
        const freeOrderStatuses = [
            EOrderStatus.created,
            EOrderStatus.charge_pending,
            EOrderStatus.charge_succeed,
            EOrderStatus.charge_failed
        ]
        return statuses.filter(status => freeOrderStatuses.includes(status))
    }

    return statuses
}

export const getStatusDisplayText = (status: EOrderStatus, isFree?: boolean): string => {
    if (isFree) {
        switch (status) {
            case EOrderStatus.charge_pending:
                return 'Award Pending'
            case EOrderStatus.charge_succeed:
                return 'Completed Successfully'
            case EOrderStatus.charge_failed:
                return 'Failed'
            default:
                break
        }
    }

    return statusDictionary[status]?.text || status
}

export const groupStatusesByText = (statusesWithText: Array<{ text: string; status: EOrderStatus }>): StatusOption[] => {
    const grouped = statusesWithText.reduce((acc, { text, status }) => {
        if (!acc[text]) {
            acc[text] = { text, statuses: [] }
        }
        acc[text].statuses.push(status)
        return acc
    }, {} as Record<string, { text: string; statuses: EOrderStatus[] }>)

    return Object.values(grouped).map(item => ({
        text: item.text,
        statuses: item.statuses.join(ordersUtils.STATUSES_SEPARATOR)
    }))
}

export const sortStatusOptionsAlphabetically = (options: Array<{ text: string; status: EOrderStatus }>): Array<{ text: string; status: EOrderStatus }> => {
    return options.sort((a, b) => {
        const textA = a.text.toLowerCase()
        const textB = b.text.toLowerCase()
        return textA.localeCompare(textB)
    })
}

export const createStatusFilterOptions = (validStatuses: EOrderStatus[], isFree?: boolean): StatusOption[] => {
    const filteredStatuses = filterStatusesByOrderType(validStatuses, isFree)

    const statusesWithText = filteredStatuses.map(status => ({
        text: getStatusDisplayText(status, isFree),
        status
    }))

    const sortedStatuses = sortStatusOptionsAlphabetically(statusesWithText)
    const groupedOptions = groupStatusesByText(sortedStatuses)

    return groupedOptions
}